/** Layout-Bausteine der Verwaltung (sc-*-Klassen aus screens.css): PageHeader, Card, KpiTile, Grid, Split, Tabs, FormGrid, Select. */
import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { NavLink } from "react-router";

export function PageHeader({ title, kicker, sub, actions }: { title: ReactNode; kicker?: ReactNode; sub?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="sc-pagehead">
      <div className="sc-col" style={{ gap: 2 }}>
        {kicker ? <span className="sc-muted sc-small">{kicker}</span> : null}
        <h1>{title}</h1>
        {sub ? <span className="sc-muted">{sub}</span> : null}
      </div>
      {actions ? <div className="sc-toolbar">{actions}</div> : null}
    </div>
  );
}

export function Card({ title, actions, flush, children, className, id }: { title?: ReactNode; actions?: ReactNode; flush?: boolean; children: ReactNode; className?: string; id?: string }) {
  const hId = useId();
  return (
    <section className={["sc-card", flush && "sc-card--flush", className].filter(Boolean).join(" ")} aria-labelledby={title ? hId : undefined} id={id}>
      {title || actions ? (
        <div className="sc-spread" style={flush ? { padding: "20px 20px 12px" } : undefined}>
          {title ? <h2 id={hId}>{title}</h2> : <span />}
          {actions}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export function KpiTile({ label, value, note }: { label: ReactNode; value: ReactNode; note?: ReactNode }) {
  return (
    <div className="sc-kpi">
      <span className="sc-kpi__label">{label}</span>
      <span className="sc-kpi__value">{value}</span>
      {note ? <span className="sc-kpi__note">{note}</span> : null}
    </div>
  );
}

export function Grid({ wide, children }: { wide?: boolean; children: ReactNode }) {
  return <div className={["sc-grid", wide && "sc-grid--wide"].filter(Boolean).join(" ")}>{children}</div>;
}

export function Split({ main, aside }: { main: ReactNode; aside: ReactNode }) {
  return (
    <div className="sc-split">
      <div className="sc-split__main">{main}</div>
      <aside className="sc-split__aside">{aside}</aside>
    </div>
  );
}

export function Tabs({ items }: { items: { to: string; label: ReactNode; end?: boolean }[] }) {
  return (
    <nav className="sc-tabs" aria-label="Unterseiten">
      {items.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => (isActive ? "is-on" : undefined)}>
          {t.label}
        </NavLink>
      ))}
    </nav>
  );
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="sc-form">{children}</div>;
}

export function Select({ label, hint, id, children, ...rest }: { label: string; hint?: string } & SelectHTMLAttributes<HTMLSelectElement>) {
  const autoId = useId();
  const sid = id ?? autoId;
  return (
    <div className="rg-field">
      <label className="rg-field__label" htmlFor={sid}>
        {label}
      </label>
      <select id={sid} className="sc-select" {...rest}>
        {children}
      </select>
      {hint ? <p className="rg-field__msg">{hint}</p> : null}
    </div>
  );
}

export function Textarea({ label, hint, id, ...rest }: { label: string; hint?: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const autoId = useId();
  const tid = id ?? autoId;
  return (
    <div className="rg-field">
      <label className="rg-field__label" htmlFor={tid}>
        {label}
      </label>
      <textarea id={tid} className="sc-textarea" {...rest} />
      {hint ? <p className="rg-field__msg">{hint}</p> : null}
    </div>
  );
}

export function Dl({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="app-dl">
      {rows.map(([k, v], i) => (
        <div key={i} style={{ display: "contents" }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
